import { Card, Typography, Radio, InputNumber, Table, Button } from "antd";
import { useState, useMemo, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";

const PT_AMOUNT = 0;

export default function SalaryCalculator({ amount, cid, is_pf, is_esi, salary_type }) {
  const { backUrl, authData } = useContext(AuthContext);

  const [salaryType, setSalaryType] = useState(salary_type || "CTC");
  const [totalAmount, setTotalAmount] = useState(amount || 0);
  const [esi, setEsi] = useState(is_esi || "Yes");
  const [pf, setPf] = useState(is_pf || "Yes");
  const [isUserEditing, setIsUserEditing] = useState(false);

  const [components, setComponents] = useState({
    basic: 0,
    hra: 0,
    conv: 0,
    allow: 0,
    gross: 0,
    ctc: 0,
    pf_er: 0,
    esi_er: 0,
    pf_ee: 0,
    esi_ee: 0
  });

  /* ---------------- Core Calculation (UNCHANGED) ---------------- */
  const calculateFromCTC = (ctc) => {
    const gross = ctc <= 23447
      ? Math.round((ctc * 115.25) / 128.677498)
      : ctc - 1800;

    return calculateFromGross(gross);
  };

  const calculateFromGross = (gross) => {
    const basic = Math.round(gross * 0.5);
    const hra = Math.round(gross * 0.3);
    const conv = Math.round(gross * 0.1);
    const allow = gross - basic - hra - conv;

    const pfBase = Math.min(basic + conv + allow, 15000);
    const pf_er = pf === "Yes" ? Math.round((pfBase * 12) / 100) : 0;
    const pf_ee = pf_er;

    const esi_er = esi === "Yes" && gross <= 21000 ? Math.round((gross * 3.25) / 100) : 0;
    const esi_ee = esi === "Yes" && gross <= 21000 ? Math.round((gross * 0.75) / 100) : 0;

    const ctc = gross + pf_er + esi_er;

    return { basic, hra, conv, allow, gross, ctc, pf_er, pf_ee, esi_er, esi_ee };
  };

  useEffect(() => {
    if (!isUserEditing && totalAmount) {
      const data =
        salaryType === "CTC"
          ? calculateFromCTC(totalAmount)
          : calculateFromGross(totalAmount);

      setComponents(data);
    }
  }, [totalAmount, salaryType]);

  /* ---------------- Derived ---------------- */
  const net = useMemo(
    () => components.gross - components.pf_ee - components.esi_ee,
    [components]
  );

  /* ---------------- Which field is user-editable in the table ----------------
     CTC mode  -> only the "ctc" row is editable, everything else auto-calculates
     Gross mode -> only the "gross" row is editable, everything else auto-calculates
     Net mode (if used) -> nothing is editable, since there is no reverse calc from Net
  */
  const editableKey =
    salaryType === "CTC" ? "ctc" : salaryType === "Gross" ? "gross" : null;

  /* ---------------- Editable Handler ---------------- */
  const handleChange = (value, key) => {
    setIsUserEditing(true);

    if (key === "ctc") {
      setTotalAmount(value || 0);
      setComponents(calculateFromCTC(value || 0));
      return;
    }

    if (key === "gross") {
      setTotalAmount(value || 0);
      setComponents(calculateFromGross(value || 0));
      return;
    }

    setComponents(prev => ({ ...prev, [key]: value || 0 }));
  };

  /* ---------------- Submit ---------------- */
  const handleEditSubmit = async () => {
    const salaryData = {
      basic_pm: components.basic,
      hra_pm: components.hra,
      conveyance_pm: components.conv,
      other_allowance_pm: components.allow,

      gross_pm: components.gross,
      ctc_pm: components.ctc,
      ctc_py: components.ctc * 12,

      pf_employer_pm: components.pf_er,
      esi_employer_pm: components.esi_er,
      pf_employee_pm: components.pf_ee,
      esi_employee_pm: components.esi_ee,

      deductions_pm: components.pf_ee + components.esi_ee + PT_AMOUNT,
      net_pay_pm: net - PT_AMOUNT,

      gross_py: components.gross * 12,
      net_pay_py: (net - PT_AMOUNT) * 12,

      offered_salary: components.ctc,
      salary_type: salaryType
    };

    await axios.patch(
      `${backUrl}/api/emp/update-salary/${cid}/`,
      salaryData,
      { headers: { Authorization: `Bearer ${authData?.access}` } }
    );

    alert("Salary updated successfully");
  };

  /* ---------------- Table ---------------- */
  const dataSource = [
    ["Basic", "basic"],
    ["HRA", "hra"],
    ["Conveyance", "conv"],
    ["Allowances", "allow"],
    ["Gross Wages", "gross"],
    ["CTC", "ctc"],
    ["PF (Employer)", "pf_er"],
    ["ESI (Employer)", "esi_er"],
    ["PF (Employee)", "pf_ee"],
    ["ESI (Employee)", "esi_ee"],
    // ["PT Deduction", null],
    ["Net Take Home", null]
  ].map(([label, key], i) => ({
    key: i,
    component: label,
    monthly: key ? components[key] : label === "PT Deduction" ? PT_AMOUNT : net - PT_AMOUNT,
    yearly: key
      ? components[key] * 12
      : label === "PT Deduction"
        ? PT_AMOUNT * 12
        : (net - PT_AMOUNT) * 12,
    stateKey: key
  }));

  const columns = [
    { title: "Component", dataIndex: "component", render: t => <b>{t}</b> },
    {
      title: "Monthly (₹)",
      dataIndex: "monthly",
      align: "center",
      render: (val, record) =>
        record.stateKey && record.stateKey === editableKey ? (
          <InputNumber
            value={val}
            min={0}
            onChange={v => handleChange(v, record.stateKey)}
            style={{ width: "60%" }}
          />
        ) : (
          val.toLocaleString("en-IN")
        )
    },
    {
      title: "Yearly (₹)",
      dataIndex: "yearly",
      align: "center",
      render: v => v.toLocaleString("en-IN")
    }
  ];

  return (
    <Card>
      <Radio.Group
        value={salaryType}
        onChange={e => {
          setSalaryType(e.target.value);
          setIsUserEditing(false);
        }}
      >
        <Radio.Button value="CTC">CTC</Radio.Button>
        <Radio.Button value="Gross">Gross</Radio.Button>
        <Radio.Button value="Net">Net</Radio.Button>
      </Radio.Group>

      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        bordered
        style={{ marginTop: 16 }}
      />

      <Button type="primary" onClick={handleEditSubmit} style={{ marginTop: 16 }}>
        Submit
      </Button>
    </Card>
  );
}